import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { Button, ButtonProps } from "@campgladiator/cgui-core.atoms.button";
import { Divider } from "@campgladiator/cgui-core.atoms.divider";
import { Icon } from "@campgladiator/cgui-core.atoms.icon";
import { Heading } from "@campgladiator/cgui-core.atoms.typography";
import { breakpoints } from "@campgladiator/cgui-core.utilities.helpers.resolution-variables";
import { Tooltip, TooltipProps } from "@campgladiator/cgui-core.atoms.tooltip";
import { useDeviceWidth } from "@campgladiator/cgui-core.utilities.hooks.use-device-width";
import { useOutsideClick } from "@campgladiator/cgui-core.utilities.hooks.use-outside-click";
import styles from "./modal.module.scss";

export type ModalProps = {
  /**
   * Controls the appearance of all modal buttons. Accepts an array type of Buttons from the Button component
   */
  buttons?: ButtonProps[];
  /**
   * DEPRECATED: This property will be deprecated, use the buttons property instead.
   */
  buttonPrimary?: ButtonProps;
  /**
   * DEPRECATED: This property will be deprecated, use the buttons property instead.
   */
  buttonSecondary?: ButtonProps;
  /**
   * This is the boolean value that controls whether or not the modal is visible.
   */
  isVisible: boolean;
  /**
   * Callback prop allowing you to pass a function that controls behavior when the close icon is clicked
   */
  onClose: () => void;
  /**
   * Controls the theme of the modal which mainly affects the color of the buttons.
   */
  theme?: "core" | "trainer";
  /**
   * Controls the title of the modal. This is a controlled design element so you cannot override this with a different element or elements.
   */
  title: string;
  /**
   * Controls the tooltip of the modal.
   */
  tooltip?: TooltipProps;
} & React.HTMLAttributes<HTMLDivElement>;

const { desktop, tabletMedium } = breakpoints;

export function Modal({
  buttons,
  buttonPrimary,
  buttonSecondary,
  children,
  className,
  isVisible,
  onClose = () => {},
  theme = "core",
  title,
  tooltip,
  ...props
}: ModalProps) {
  const [buttonSize, setButtonSize] = useState<ButtonProps["size"]>("default");
  const [isTooltipVisible, setIsTooltipVisible] = useState<boolean>(false);

  const deviceWidth = useDeviceWidth();

  const tooltipRef = useRef<HTMLDivElement>(null);

  useOutsideClick({
    ref: tooltipRef,
    callback: () => setIsTooltipVisible(false),
  });

  const handleEscapeKeyPress = ({ key }) =>
    (key === "Escape" || key === "Esc") && onClose();

  const handleTooltipClick = () => {
    if (isMobileBreakpoint) {
      setIsTooltipVisible((prevIsTooltipVisible) => !prevIsTooltipVisible);
    }
  };

  useEffect(() => {
    window.addEventListener("keyup", handleEscapeKeyPress);

    return () => {
      window.removeEventListener("keyup", handleEscapeKeyPress);
    };
  }, []);

  useEffect(() => {
    if (deviceWidth >= desktop) {
      setButtonSize("large");
    } else {
      setButtonSize("default");
    }
  }, [deviceWidth]);

  const hasButtonList: boolean = buttons?.length > 0;
  const hasPrimaryOrSecondaryButton: boolean =
    !!buttonPrimary || !!buttonSecondary;
  const isMobileBreakpoint = deviceWidth < tabletMedium;
  const tooltipClass = `${styles.tooltip} ${
    isMobileBreakpoint
      ? isTooltipVisible
        ? styles.visible
        : styles.hidden
      : styles.hoverVisible
  }`;

  return (
    isVisible && (
      <div
        {...props}
        className={styles.overlay}
        onClick={onClose}
        data-theme={theme}
      >
        <section
          className={classNames(styles.modal, className)}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={styles.content}>
            <div className={styles.heading}>
              <Heading type="h2" className={styles.title}>
                {title}
              </Heading>
              {tooltip && (
                <div
                  ref={tooltipRef}
                  className={styles.tooltipContainer}
                  onClick={handleTooltipClick}
                >
                  <Icon.Line
                    name="icon-question-circle"
                    className={styles.tooltipIcon}
                  />
                  <Tooltip
                    className={classNames(tooltipClass, tooltip.className)}
                    {...tooltip}
                  />
                </div>
              )}
            </div>
            <div className={styles.body}>{children}</div>
          </div>
          {(hasPrimaryOrSecondaryButton || hasButtonList) && (
            <>
              <Divider />
              <footer className={styles.buttons}>
                {hasButtonList &&
                  buttons?.map((btn) => (
                    <Button
                      emphasis={btn.emphasis}
                      size={btn.size}
                      variation={btn.variation}
                      className={classNames(styles.action, btn.className)}
                      disabled={btn.disabled}
                      icon={btn.icon}
                      onClick={btn.onClick}
                      theme={btn.theme}
                    >
                      {btn.children}
                    </Button>
                  ))}
                {buttonSecondary && (
                  <Button
                    emphasis="primary"
                    size={buttonSize}
                    variation="text"
                    className={classNames(
                      styles.action,
                      buttonSecondary.className
                    )}
                    disabled={buttonSecondary.disabled}
                    icon={buttonSecondary.icon}
                    onClick={buttonSecondary.onClick}
                  >
                    {buttonSecondary.children}
                  </Button>
                )}
                {buttonPrimary && (
                  <Button
                    emphasis="primary"
                    size={buttonSize}
                    variation="solid"
                    className={classNames(
                      styles.action,
                      buttonPrimary.className
                    )}
                    disabled={buttonPrimary.disabled}
                    icon={buttonPrimary.icon}
                    onClick={buttonPrimary.onClick}
                  >
                    {buttonPrimary.children}
                  </Button>
                )}
              </footer>
            </>
          )}
          <div className={styles.close}>
            <Icon.Line name="icon-times" onClick={onClose} />
          </div>
        </section>
      </div>
    )
  );
}
